import { useState } from "react";

import {
  ArrowBackIos,
  ArrowForwardIos,
  CancelTwoTone,
  CloudDownload,
  Edit,
} from "@mui/icons-material";
import { Box, IconButton, Modal, Tooltip, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { colors } from "@rubi/ui";

import ImageEditor from "./ImageEditor";
import LazyLoadVideo from "./LazyLoadVideo";

const AttachmentPreviewModal = ({
  open,
  onClose,
  mediaSrc,
  mediaType,
  fileName,
  onDownload,
  onEditSave,
  currentIndex,
  totalCount,
  onNavigate,
}: {
  open: boolean;
  onClose: () => void;
  mediaSrc: string;
  mediaType: "image" | "video" | "pdf";
  fileName?: string;
  onDownload?: () => void;
  onEditSave?: (file: File) => void;
  currentIndex?: number;
  totalCount?: number;
  onNavigate?: (index: number) => void;
}) => {
  const [isEditing, setIsEditing] = useState(false);

  const handleClose = () => {
    setIsEditing(false);
    onClose();
  };

  const handleEditSave = (file: File) => {
    setIsEditing(false);
    onEditSave?.(file);
  };

  const hasPaging =
    onNavigate &&
    totalCount !== undefined &&
    currentIndex !== undefined &&
    totalCount > 1;

  if (isEditing && mediaType === "image" && onEditSave) {
    return (
      <Modal open={open} onClose={handleClose}>
        <Box
          sx={{
            position: "absolute",
            inset: 0,
          }}
        >
          <ImageEditor
            imageUrl={mediaSrc}
            fileName={fileName || "edited-image.jpg"}
            onSave={handleEditSave}
            onCancel={() => setIsEditing(false)}
          />
        </Box>
      </Modal>
    );
  }

  return (
    <Modal open={open} onClose={handleClose}>
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: alpha(colors.common.black, 0.85),
        }}
        onClick={handleClose}
      >
        {/* Close button */}
        <IconButton
          onClick={handleClose}
          sx={{
            position: "absolute",
            top: 12,
            right: 12,
            color: colors.common.white,
            zIndex: 1,
          }}
        >
          <CancelTwoTone style={{ fontSize: 36 }} />
        </IconButton>

        {/* Download button */}
        {onDownload && (
          <Tooltip title="Download" placement="left">
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                onDownload();
              }}
              sx={{
                position: "absolute",
                top: 12,
                right: 60,
                color: colors.common.white,
                zIndex: 1,
              }}
            >
              <CloudDownload style={{ fontSize: 32 }} />
            </IconButton>
          </Tooltip>
        )}

        {/* Edit button */}
        {onEditSave && mediaType === "image" && (
          <Tooltip title="Edit image" placement="left">
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                setIsEditing(true);
              }}
              sx={{
                position: "absolute",
                top: 12,
                right: onDownload ? 108 : 60,
                color: colors.common.white,
                zIndex: 1,
              }}
            >
              <Edit style={{ fontSize: 32 }} />
            </IconButton>
          </Tooltip>
        )}

        {/* Previous arrow */}
        {hasPaging && (
          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              if (currentIndex > 0) onNavigate(currentIndex - 1);
            }}
            disabled={currentIndex === 0}
            sx={{
              position: "absolute",
              left: 12,
              top: "50%",
              transform: "translateY(-50%)",
              color: colors.common.white,
              zIndex: 1,
              "&.Mui-disabled": { color: alpha(colors.common.white, 0.3) },
            }}
          >
            <ArrowBackIos style={{ fontSize: 36 }} />
          </IconButton>
        )}

        {/* Next arrow */}
        {hasPaging && (
          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              if (currentIndex < totalCount - 1) onNavigate(currentIndex + 1);
            }}
            disabled={currentIndex === totalCount - 1}
            sx={{
              position: "absolute",
              right: 12,
              top: "50%",
              transform: "translateY(-50%)",
              color: colors.common.white,
              zIndex: 1,
              "&.Mui-disabled": { color: alpha(colors.common.white, 0.3) },
            }}
          >
            <ArrowForwardIos style={{ fontSize: 36 }} />
          </IconButton>
        )}

        {/* Media content */}
        <Box
          onClick={(e) => e.stopPropagation()}
          sx={{
            maxWidth: "90vw",
            maxHeight: "90vh",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          {mediaType === "pdf" ? (
            <iframe
              src={mediaSrc}
              title={fileName || "PDF preview"}
              style={{
                width: "80vw",
                height: "85vh",
                border: "none",
              }}
            />
          ) : mediaType === "image" ? (
            <img
              src={mediaSrc}
              alt={fileName || "Attachment preview"}
              style={{
                maxWidth: "90vw",
                maxHeight: "85vh",
                objectFit: "contain",
              }}
            />
          ) : (
            <LazyLoadVideo
              src={mediaSrc}
              style={{ overflow: "visible" }}
              videoStyle={{
                width: "auto",
                height: "auto",
                maxWidth: "90vw",
                maxHeight: "85vh",
              }}
            />
          )}
          {/* File name and paging counter */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              mt: 1,
            }}
          >
            {fileName && (
              <Typography
                variant="body2"
                sx={{
                  color: colors.common.white,
                  textAlign: "center",
                  maxWidth: "70vw",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {fileName}
              </Typography>
            )}
            {hasPaging && (
              <Typography
                variant="body2"
                sx={{ color: alpha(colors.common.white, 0.7) }}
              >
                {currentIndex + 1} / {totalCount}
              </Typography>
            )}
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};

export default AttachmentPreviewModal;
